import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Layers, Plus, Trash2, AlertTriangle, Calendar, Package } from "lucide-react";

/** Product batches management (FEFO). */
export default function Batches() {
  const [products, setProducts] = useState([]);
  const [selectedPid, setSelectedPid] = useState("");
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", batch_number: "", quantity: 0, expiry_date: "", cost_price: 0, note: "" });
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [p, b] = await Promise.all([api.get("/products"), api.get("/batches", { params: { active_only: true } })]);
      setProducts(p.data); setBatches(b.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ product_id: selectedPid || products[0]?.id || "", batch_number: "", quantity: 0, expiry_date: "", cost_price: 0, note: "" }); setOpen(true); };
  const save = async () => {
    if (!form.product_id || !form.batch_number.trim() || form.quantity <= 0) return toast.error("املأ الحقول الأساسية");
    try { await api.post("/batches", form); toast.success("تم إضافة الدفعة"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => { if (!confirm("حذف الدفعة؟")) return; try { await api.delete(`/batches/${id}`); load(); } catch (e) { toast.error(formatErr(e)); } };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return batches.filter(b => {
      if (selectedPid && b.product_id !== selectedPid) return false;
      if (query && !b.product_name.toLowerCase().includes(query) && !b.batch_number.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [batches, q, selectedPid]);

  const nearExp = (d) => { if (!d) return false; const days = (new Date(d) - new Date()) / 86400000; return days > 0 && days <= 30; };
  const expired = (d) => d && new Date(d) < new Date();

  return (
    <div className="space-y-4" data-testid="batches-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><Layers className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">دفعات الأصناف (FEFO)</h1>
          <p className="text-sm text-muted-foreground">إدارة دفعات الأدوية بتاريخ الصلاحية — الأقرب انتهاءً يُباع أولاً</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-batch-btn"><Plus className="w-4 h-4" /> دفعة جديدة</Button>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="ابحث باسم الصنف أو رقم الدفعة..." data-testid="batch-search" />
        </div>
        <Select value={selectedPid} onValueChange={setSelectedPid}>
          <SelectTrigger data-testid="batch-product-filter"><SelectValue placeholder="كل الأصناف" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">كل الأصناف</SelectItem>
            {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>الصنف</TableHead><TableHead>رقم الدفعة</TableHead>
            <TableHead>الكمية المتبقية</TableHead><TableHead>تاريخ الصلاحية</TableHead>
            <TableHead>سعر التكلفة</TableHead><TableHead>إجراءات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map(b => (
              <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                <TableCell className="font-bold flex items-center gap-1"><Package className="w-3 h-3" /> {b.product_name}</TableCell>
                <TableCell className="font-mono text-xs">{b.batch_number}</TableCell>
                <TableCell><span className={b.remaining < b.initial_quantity * 0.2 ? "text-amber-600 font-bold" : ""}>{b.remaining} / {b.initial_quantity}</span></TableCell>
                <TableCell className="text-xs">
                  <span className={`inline-flex items-center gap-1 ${expired(b.expiry_date) ? "text-red-600 font-bold" : nearExp(b.expiry_date) ? "text-amber-600 font-bold" : ""}`}>
                    <Calendar className="w-3 h-3" />
                    {b.expiry_date ? new Date(b.expiry_date).toLocaleDateString("ar-EG") : "—"}
                    {expired(b.expiry_date) && <AlertTriangle className="w-3 h-3" title="منتهي!" />}
                  </span>
                </TableCell>
                <TableCell>{b.cost_price?.toLocaleString("ar-EG") || "—"}</TableCell>
                <TableCell><Button size="sm" variant="ghost" onClick={()=>del(b.id)} className="h-7 text-destructive" data-testid={`del-batch-${b.id}`}><Trash2 className="w-3 h-3" /></Button></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">لا توجد دفعات</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="batch-dialog">
          <DialogHeader><DialogTitle>دفعة جديدة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>الصنف</Label>
              <Select value={form.product_id} onValueChange={(v)=>setForm({...form, product_id: v})}>
                <SelectTrigger data-testid="batch-product-select"><SelectValue placeholder="اختر" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>رقم الدفعة</Label><Input value={form.batch_number} onChange={(e)=>setForm({...form, batch_number:e.target.value})} data-testid="batch-num-input" /></div>
              <div><Label>الكمية</Label><Input type="number" value={form.quantity} onChange={(e)=>setForm({...form, quantity:+e.target.value})} data-testid="batch-qty-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>تاريخ الصلاحية</Label><Input type="date" value={form.expiry_date} onChange={(e)=>setForm({...form, expiry_date:e.target.value})} data-testid="batch-exp-input" /></div>
              <div><Label>سعر التكلفة</Label><Input type="number" step="0.01" value={form.cost_price} onChange={(e)=>setForm({...form, cost_price:+e.target.value})} data-testid="batch-cost-input" /></div>
            </div>
            <div><Label>ملاحظة</Label><Input value={form.note} onChange={(e)=>setForm({...form, note:e.target.value})} data-testid="batch-note-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-batch-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
